/* tslint:disable object-literal-sort-keys */
import { get, keyBy, sumBy } from 'lodash';

import { TableRenderingUtils } from '../../../utils';
import { ILoan, ILoanPayment, IReactTableColumn } from '../../../interfaces';

const { renderDate, renderMoney, renderSummedAmount, renderTotalRowCount, renderUnformatted } = TableRenderingUtils;

/**
 * The columns of the account's balance payments.
 *
 * A payment is split across loans, so the interest and principal columns sum its allocations rather
 * than reading a field: the split lives one level down, on the allocations, and there is no total of
 * it on the payment itself. Summed through an accessor rather than in the Cell so the columns sort
 * by what they show.
 *
 * `loans` is only here to name the loans a payment touched.
 */
function loanPaymentColumns (loans: ILoan[]): IReactTableColumn[] {
  const loansById = keyBy(loans, 'id');

  function interestPaid (payment: ILoanPayment) {
    return sumBy(payment.allocations || [], 'amount_to_interest');
  }

  function principalPaid (payment: ILoanPayment) {
    return sumBy(payment.allocations || [], 'amount_to_principal');
  }

  // Loans are known to users by their vanity id, as they are on the loans table. A payment usually
  // touches one loan; the waterfall spreads it further only when the first cannot absorb it.
  function loansApplied (payment: ILoanPayment) {
    return (payment.allocations || [])
      .map(allocation => get(loansById[allocation.loan], 'vanity_id'))
      .filter(vanityId => vanityId !== undefined)
      .join(', ');
  }

  return [
    {
      Header: 'Payment Date',
      columns: [{
        Header: renderTotalRowCount,
        accessor: 'payment_date',
        Cell: renderDate,
        minWidth: 120,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Amount',
      columns: [{
        Header: renderSummedAmount,
        accessor: 'amount',
        Cell: renderMoney,
        minWidth: 110,
        maxWidth: 200,
      }],
    },
    {
      Header: 'Interest',
      columns: [{
        Header: renderSummedAmount,
        id: 'interest_paid',
        accessor: interestPaid,
        Cell: renderMoney,
        minWidth: 110,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Principal',
      columns: [{
        Header: renderSummedAmount,
        id: 'principal_paid',
        accessor: principalPaid,
        Cell: renderMoney,
        minWidth: 110,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Loans',
      columns: [{
        id: 'loans_applied',
        accessor: loansApplied,
        Cell: renderUnformatted,
        minWidth: 100,
        maxWidth: 200,
        sortable: false,
      }],
    },
    {
      Header: 'Notes',
      columns: [{
        accessor: 'notes',
        Cell: renderUnformatted,
        minWidth: 160,
        sortable: false,
      }],
    },
  ];
}

export { loanPaymentColumns };
